/**
 * CreateWorkflowDialogHost
 *
 * Singleton host for the global Create Workflow flow. Mounted once in
 * `AppLayout` so any caller — header pill, list-page hero, designer page —
 * can open the flow by calling `openCreateWorkflowDialog()` without changing
 * routes.
 *
 * The host orchestrates a 3-stage flow:
 *
 *   1. **Saving draft** (conditional) — if the user is inside a workflow
 *      that has unsaved draft changes when they click "Create new workflow"
 *      from the header pill, we first persist their draft so the new
 *      workflow doesn't silently strand recent edits. Shown as a
 *      `SavingDraftOverlay`. Failures land on a recovery surface with
 *      Try again / Continue anyway / Cancel.
 *
 *   2. **Naming** (always) — the existing `CreateWorkflowDialog` collects
 *      the name and template.
 *
 *   3. **Routing prompt** (conditional) — if the flow started while the
 *      user was inside another workflow, ask whether to open the new
 *      workflow or stay on the current one (`WorkflowCreatedPrompt`).
 *      The legacy behaviour silently navigated; this preserves the
 *      user's place in the workflow they were authoring.
 *
 * Stages 1 and 3 are skipped when there's no current workflow (e.g. the
 * dialog was opened from the workflow-list hero or a `?create=1` deep
 * link) — there's nothing to save and nothing to "stay on".
 *
 * Centralising the mutation + post-create routing here removes the
 * previous "navigate to list page → open dialog → navigate to designer"
 * bounce when the user clicks "+ Create new workflow" from inside the
 * designer, and keeps the create flow consistent across surfaces.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useCreateWorkflowFromPromptEnabled } from '@/hooks/useWorkflowAssistantFeatures';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useCreateWorkflow } from '@/hooks/useCreateWorkflow';
import { useDraftSave } from '@/hooks/useDraftSave';
import { getIsDraftDirty } from '@/hooks/useIsDraftDirty';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import {
  useCreateWorkflowDialogStore,
  closeCreateWorkflowDialog,
  type CreateWorkflowDialogSource,
} from '@/stores/createWorkflowDialogStore';
import { useTelemetry } from '@/hooks/useTelemetry';
import { paths } from '@/routes';
import { extractWorkflowName } from '@/types/logicApps';
import type { DraftSaveResult } from '@/types/draftMode';
import { CreateWorkflowDialog } from '@/components/CreateWorkflowDialog';
import { SavingDraftOverlay } from '@/components/CreateWorkflowFlow/SavingDraftOverlay';
import { WorkflowCreatedPrompt } from '@/components/CreateWorkflowFlow/WorkflowCreatedPrompt';
import type { CreateWorkflowFlowPhase } from '@/components/CreateWorkflowFlow/types';

/**
 * DraftSaveDriver — invisible worker that owns the `useDraftSave` hook.
 *
 * Lives in its own component so its Zustand subscriptions (workflow store,
 * connections store, draft-mode store) are mounted ONLY during the saving /
 * save-error phases. Keeping these subscriptions out of `CreateWorkflowDialogHost`
 * — which is mounted persistently in `AppLayout` — means every keystroke
 * in the designer no longer re-renders the host.
 *
 * The parent triggers a save by bumping `saveRequestId`. The driver kicks
 * off the save in an effect and reports the outcome via `onComplete`.
 * The latest callback references are read through a ref so identity
 * changes in `onComplete` / `trackEvent` / `save` don't double-fire the
 * effect — only `saveRequestId` is an intentional trigger.
 */
interface DraftSaveDriverProps {
  workflowName: string;
  saveRequestId: number;
  source: CreateWorkflowDialogSource;
  onComplete: (result: DraftSaveResult) => void;
  trackEvent: (name: string, props?: Record<string, unknown>) => void;
}

const DraftSaveDriver = ({
  workflowName,
  saveRequestId,
  source,
  onComplete,
  trackEvent,
}: DraftSaveDriverProps) => {
  const { save } = useDraftSave({ workflowName });
  // Latest-callbacks ref pattern. Writing to the ref in `useLayoutEffect`
  // (rather than during render) keeps the assignment out of the
  // render-time side-effect surface — strict-mode-safe under double
  // invocation, and unambiguous in concurrent rendering. Layout effect
  // runs after commit but before paint, so the save trigger effect
  // below (a `useEffect`, scheduled after paint) always reads the
  // latest closures.
  const latest = useRef({ save, onComplete, trackEvent, source });
  useLayoutEffect(() => {
    latest.current = { save, onComplete, trackEvent, source };
  });

  useEffect(() => {
    if (saveRequestId === 0) return;
    let cancelled = false;
    const { save: doSave, onComplete: report, trackEvent: track, source: src } =
      latest.current;
    track('workflow.create.save_draft_started', { source: src });
    const start = performance.now();
    void (async () => {
      const result = await doSave();
      if (cancelled) return;
      const duration_ms = Math.round(performance.now() - start);
      if (result.success) {
        track('workflow.create.save_draft_succeeded', {
          source: src,
          duration_ms,
        });
      } else {
        track('workflow.create.save_draft_failed', {
          source: src,
          error: result.error ?? 'unknown',
        });
      }
      report(result);
    })();
    return () => {
      cancelled = true;
    };
    // `saveRequestId` is the only intentional trigger here. Identity
    // changes in `save` / `onComplete` / `trackEvent` are read through the
    // ref above so they don't double-fire the effect.
  }, [saveRequestId]);

  return null;
};

export const CreateWorkflowDialogHost = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();
  const { projectName, locationName } = useCurrentProject();
  const { appName, app } = useCurrentApp();
  // Pulling the workflow name from URL params (rather than the workflow
  // store) gives us a single source of truth for "are we currently
  // viewing a workflow?". When the user is on the app workflows list
  // there's no `workflowName` segment and we treat both Stage 1 and
  // Stage 3 as no-ops.
  const { tenantId, workflowName: currentWorkflowName } = useParams<{
    tenantId: string;
    workflowName: string;
  }>();
  const { canEdit, isLoading: isPermissionsLoading } = useAppPermission();
  const isAssistantEnabled = useCreateWorkflowFromPromptEnabled();
  const queuePrompt = useAgentChatAguiStore((s) => s.queuePrompt);

  const isOpen = useCreateWorkflowDialogStore((s) => s.isOpen);
  const pendingPrompt = useCreateWorkflowDialogStore((s) => s.pendingPrompt);
  const source = useCreateWorkflowDialogStore((s) => s.source);
  const openTemplates = useCreateWorkflowDialogStore((s) => s.openTemplates);
  const closeDialog = useCreateWorkflowDialogStore((s) => s.close);
  const isToolbarCreationUnavailable = source === 'workflow-list-toolbar' &&
    (!canEdit || isPermissionsLoading || (!!pendingPrompt && !isAssistantEnabled));

  const { data: workflows } = useAppWorkflows(app ?? null);
  const [createError, setCreateError] = useState<string | null>(null);

  // ── Flow phase + post-create routing context ────────────────────────────
  const [phase, setPhase] = useState<CreateWorkflowFlowPhase>('idle');
  // Captured at the moment the flow starts: did the user trigger this
  // *while* viewing another workflow? If yes, we offer the routing
  // prompt after create instead of silently navigating. Pinned to the
  // open transition so subsequent URL changes during the flow don't
  // disrupt the post-create branching.
  const [flowStartedInsideWorkflow, setFlowStartedInsideWorkflow] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [pendingNewWorkflowName, setPendingNewWorkflowName] = useState<string | null>(null);
  // Monotonic counter the host bumps to request a new draft-save from the
  // `DraftSaveDriver` child. The driver is the only thing subscribed to
  // the workflow stores, keeping the host's render frequency low.
  const [saveRequestId, setSaveRequestId] = useState(0);

  const canBuildDesignerPath = !!(locationName && projectName && appName);

  const getDesignerPath = useCallback(
    (workflowName: string) => {
      if (canBuildDesignerPath) {
        return paths.appWorkflowDesigner(locationName!, projectName!, appName!, workflowName, tenantId);
      }
      return '#';
    },
    [locationName, projectName, appName, canBuildDesignerPath, tenantId],
  );

  // Compose the final navigation target: the designer path plus the
  // optional `?openTemplates=true` query-string that the template-mode
  // entrypoint relies on (added in #1357). Centralised so both
  // navigation sites — the immediate post-create path and the
  // routing-prompt's "Open new workflow" handler — honour the flag.
  const buildDesignerNavTarget = useCallback(
    (workflowName: string) => {
      const designerPath = getDesignerPath(workflowName);
      return openTemplates ? `${designerPath}?openTemplates=true` : designerPath;
    },
    [getDesignerPath, openTemplates],
  );

  // ── Draft save ─────────────────────────────────────────────────────────
  // The actual `useDraftSave` hook lives in `DraftSaveDriver` (below)
  // so its workflow-store subscriptions only mount during stages 1+1b.
  // The host requests a save by bumping `saveRequestId` and reacts to
  // the driver's result via `handleSaveComplete`.
  const requestDraftSave = useCallback(() => {
    setSaveErrorMessage(null);
    setPhase('saving');
    setSaveRequestId((n) => n + 1);
  }, []);

  const handleSaveComplete = useCallback((result: DraftSaveResult) => {
    if (result.success) {
      setPhase('naming');
    } else {
      setSaveErrorMessage(result.error ?? null);
      setPhase('save-error');
    }
  }, []);

  // ── Create mutation ────────────────────────────────────────────────────
  const createMutation = useCreateWorkflow({
    appInfo: { projectName: projectName ?? '', appName: appName ?? '' },
    onSuccess: (workflowName) => {
      trackEvent('workflow.create.succeeded', { workflowName, source });
      if (pendingPrompt) {
        queuePrompt(
          pendingPrompt,
          { type: 'workflow' },
          source === 'workflow-list-toolbar' ? 'workflow-list-toolbar' : 'workflow-list-hero',
        );
      }
      // Invalidate so the list page refreshes if it's mounted underneath
      // and the routing-prompt's leaf-pill refetch picks up the new entry.
      if (projectName && appName) {
        queryClient.invalidateQueries({
          queryKey: ['workflows', projectName.toLowerCase(), appName.toLowerCase()],
        });
      }
      setCreateError(null);

      if (flowStartedInsideWorkflow) {
        // Stage 3 — defer navigation until the user picks an option.
        setPendingNewWorkflowName(workflowName);
        setPhase('created-prompt');
        trackEvent('workflow.create.routing_prompt_shown', {
          newWorkflow: workflowName,
        });
      } else {
        // No current workflow at start → behave as before: navigate to
        // the new workflow's designer and close the flow.
        closeDialog();
        setPhase('idle');
        if (canBuildDesignerPath) {
          navigate(buildDesignerNavTarget(workflowName));
        }
      }
    },
    onError: (err) => {
      setCreateError(err.message || 'Failed to create workflow');
      // Drop back from `creating` to `naming` so the user can fix and
      // retry. Keeping the dialog open is identical to the legacy flow.
      setPhase('naming');
    },
  });

  // Pull the stable primitives out of the mutation: `mutate` is
  // referentially stable across renders, `isPending` is the boolean we
  // pass to the dialog. Listing the whole `createMutation` object in
  // `useCallback` deps would defeat memoization because React Query
  // returns a fresh object reference on every render.
  const { mutate: createMutate, isPending: isCreating } = createMutation;

  useEffect(() => {
    if (isOpen && isToolbarCreationUnavailable && !isCreating) {
      trackEvent('workflow.create.access_revoked', { source });
      closeDialog();
      setCreateError(null);
    }
  }, [isOpen, isToolbarCreationUnavailable, isCreating, closeDialog, source, trackEvent]);

  // ── Naming dialog: name suggestion ─────────────────────────────────────
  const liveDefaultName = useMemo(() => {
    const names = new Set((workflows ?? []).map((w) => extractWorkflowName(w.name).toLowerCase()));
    let i = 1;
    while (names.has(`myworkflow${i}`)) i++;
    return `myWorkflow${i}`;
  }, [workflows]);

  // ── Open transition: decide initial phase + snapshot the default name ──
  // Mirrors the existing `frozenDefaultName` pattern (adjust-state-during-
  // render) so the flow advances on the same frame as `isOpen` flipping
  // true — no "click did nothing" gap. Doing this in `useEffect` would
  // leave `frozenDefaultName === null` for one render after the user
  // clicks the trigger, producing a perceptible "click did nothing"
  // frame. See:
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [frozenDefaultName, setFrozenDefaultName] = useState<string | null>(null);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      const hasCurrentWf = Boolean(currentWorkflowName);
      setFlowStartedInsideWorkflow(hasCurrentWf);
      setFrozenDefaultName(liveDefaultName);
      // The header pill and the ⌘K command palette both fire from inside
      // the designer, so they carry the "save before create" contract — a
      // dirty draft of the current workflow is persisted before the
      // post-create routing prompt may navigate away. Other surfaces (hero,
      // deep-link) lack that spatial context, so they skip the save.
      const shouldSave =
        hasCurrentWf &&
        (source === 'header' || source === 'command-palette') &&
        getIsDraftDirty();
      if (shouldSave) {
        // Kick off the save by bumping `saveRequestId` — the `DraftSaveDriver`
        // (rendered below when phase ∈ {saving, save-error}) picks this up
        // in its effect.
        requestDraftSave();
      } else {
        setPhase('naming');
      }
    } else {
      // Closed externally → reset.
      setPhase('idle');
      setFrozenDefaultName(null);
      setPendingNewWorkflowName(null);
      setSaveErrorMessage(null);
      setFlowStartedInsideWorkflow(false);
    }
  }

  // Reset the dialog store whenever this host loses app context so a
  // stale `isOpen=true` from a prior app session can't leak into a
  // different app.
  useEffect(() => {
    if (!app) {
      closeCreateWorkflowDialog();
    }
  }, [app]);

  // ── Naming dialog handlers ─────────────────────────────────────────────
  const handleSubmit = useCallback(
    (name: string) => {
      trackEvent('workflow.create.started', { source, hasPrompt: !!pendingPrompt });
      setPhase('creating');
      createMutate(name);
    },
    [createMutate, trackEvent, pendingPrompt, source],
  );

  const handleCancel = useCallback(() => {
    if (isCreating) return;
    closeDialog();
    setCreateError(null);
    setPhase('idle');
  }, [isCreating, closeDialog]);

  // ── Stage-1 recovery handlers ──────────────────────────────────────────
  const handleSaveRetry = useCallback(() => {
    trackEvent('workflow.create.save_draft_recovery', { choice: 'retry' });
    requestDraftSave();
  }, [requestDraftSave, trackEvent]);

  const handleSaveContinue = useCallback(() => {
    trackEvent('workflow.create.save_draft_recovery', { choice: 'continue' });
    // Advance to naming without retrying the save. The user's draft
    // remains dirty in memory; the autosave loop / manual save will pick
    // it up later. Creating the new workflow is independent of the
    // current workflow's state, so this doesn't lose anything.
    setSaveErrorMessage(null);
    setPhase('naming');
  }, [trackEvent]);

  const handleSaveCancel = useCallback(() => {
    trackEvent('workflow.create.save_draft_recovery', { choice: 'cancel' });
    closeDialog();
    setSaveErrorMessage(null);
    setPhase('idle');
  }, [closeDialog, trackEvent]);

  // ── Stage-3 routing prompt handlers ────────────────────────────────────
  const handleOpenNew = useCallback(() => {
    if (!pendingNewWorkflowName) return;
    trackEvent('workflow.create.routing_prompt_choice', { choice: 'open-new' });
    const target = buildDesignerNavTarget(pendingNewWorkflowName);
    closeDialog();
    setPhase('idle');
    setPendingNewWorkflowName(null);
    if (canBuildDesignerPath) {
      navigate(target);
    }
  }, [
    pendingNewWorkflowName,
    trackEvent,
    buildDesignerNavTarget,
    closeDialog,
    canBuildDesignerPath,
    navigate,
  ]);

  const handleStay = useCallback(() => {
    trackEvent('workflow.create.routing_prompt_choice', { choice: 'stay' });
    closeDialog();
    setPhase('idle');
    setPendingNewWorkflowName(null);
  }, [closeDialog, trackEvent]);

  // ── Render ─────────────────────────────────────────────────────────────
  // Only render once we have app context AND the user can edit. Avoids
  // mounting a dialog that would immediately fail validation in
  // `useCreateWorkflow` if the user lacks permission. Also guards against
  // mounting before we've frozen a defaultName.
  if (!app || !canEdit || isToolbarCreationUnavailable || frozenDefaultName === null) return null;

  // The CreateWorkflowDialog stays mounted across stages 2 and 4 (naming +
  // creating). Stage 1 (saving / save-error) and Stage 3 (created-prompt)
  // render their own floating panels and hide the dialog.
  const showNamingDialog = phase === 'naming' || phase === 'creating';

  return (
    <>
      {(phase === 'saving' || phase === 'save-error') && currentWorkflowName && (
        <DraftSaveDriver
          workflowName={currentWorkflowName}
          saveRequestId={saveRequestId}
          source={source}
          onComplete={handleSaveComplete}
          trackEvent={trackEvent}
        />
      )}
      {phase === 'saving' && (
        <SavingDraftOverlay variant="saving" />
      )}
      {phase === 'save-error' && (
        <SavingDraftOverlay
          variant="error"
          errorMessage={saveErrorMessage}
          onRetry={handleSaveRetry}
          onContinue={handleSaveContinue}
          onCancel={handleSaveCancel}
        />
      )}
      <CreateWorkflowDialog
        open={showNamingDialog}
        defaultName={frozenDefaultName}
        app={app}
        isCreating={isCreating}
        error={createError}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
      {phase === 'created-prompt' && pendingNewWorkflowName && (
        <WorkflowCreatedPrompt
          newWorkflowName={extractWorkflowName(pendingNewWorkflowName)}
          onOpenNew={handleOpenNew}
          onStay={handleStay}
        />
      )}
    </>
  );
};
